import { useState, useEffect, useCallback } from 'react';

export interface UseOTPTimerReturn {
  resendTimer: number;
  canResend: boolean;
  startTimer: (duration?: number) => void;
  resetTimer: () => void;
  formatTime: (seconds: number) => string;
}

export interface UseOTPTimerOptions {
  initialDuration?: number;
  onTimerComplete?: () => void;
}

/**
 * Custom hook for managing OTP resend timer functionality
 * @param options Configuration options for the timer
 * @returns Timer state and control functions
 */
export const useOTPTimer = (options: UseOTPTimerOptions = {}): UseOTPTimerReturn => {
  const { initialDuration = 30, onTimerComplete } = options;
  
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(true);

  // Timer countdown effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            onTimerComplete?.();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [resendTimer, onTimerComplete]);

  /**
   * Start the timer with specified duration
   * @param duration Timer duration in seconds (defaults to initialDuration)
   */
  const startTimer = useCallback((duration: number = initialDuration) => {
    setResendTimer(duration);
    setCanResend(false);
  }, [initialDuration]);

  /**
   * Reset timer to zero and enable resend
   */
  const resetTimer = useCallback(() => {
    setResendTimer(0);
    setCanResend(true);
  }, []);

  /**
   * Format seconds into MM:SS format
   * @param seconds Number of seconds
   * @returns Formatted time string (e.g., "0:30", "1:05")
   */
  const formatTime = useCallback((seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }, []);

  return {
    resendTimer,
    canResend,
    startTimer,
    resetTimer,
    formatTime,
  };
};
